import { useEffect, useState } from "react";
import { getCartHoldState } from "./booking";
import type { CartItem } from "./cart";
import { cartHoldIds } from "./hold-timer-logic";
import { useNow } from "./use-now";
import { holdChipText, nextLapseAt, readHoldState, type HoldChip, type LineHold } from "./visit-checkout-logic";

type Read = { key: string; holds: Record<string, LineHold>; skewMs: number };

// The last answer per set of ids, kept for the page's life: coming back from
// the checkout (the cart view mounts again) shows the known length at once
// instead of a blank line that fills in one round trip later.
const lastRead = new Map<string, Read>();

/**
 * The "Your visit" step's hold chips: for each line's booking, the words its
 * chip shows (holdChipText), from the server's own hold_expires_at.
 *
 * It reads get_cart_hold_state (20260927145010: a read that writes nothing,
 * authorised by the cart token; the checkout's HoldTimer reads it too) when
 * the cart view opens and again whenever a line's booking, time or party
 * changes (an edit re-arms the hold). Between reads it counts down on the server's clock (the read returns the server's
 * now), re-rendering every 30 s. A failed read, or a server without the
 * function yet, leaves every chip at "Held while you check out": no number is
 * better than a wrong one. Before the first answer the chip keeps its line but
 * shows nothing (tone "pending"), so its words do not change in front of the
 * guest; a hold that runs out while the guest is on this step turns into the
 * checkout's lapse line at that moment, not at the next tick.
 */
export function useCartHoldChips(items: readonly CartItem[]): (bookingId: string) => HoldChip | null {
  const ids = cartHoldIds(items);
  const idKey = ids.join(",");
  // What re-arms a hold: its window and party (update_booking_hold).
  const armKey = items
    .map((it) => {
      const md = it.metadata ?? {};
      return `${String(md.booking_id ?? "")}|${String(md.starts_at ?? "")}|${String(md.ends_at ?? "")}|${String(md.party_size ?? "")}`;
    })
    .join(";");
  const [state, setState] = useState<Read | null>(() => lastRead.get(idKey) ?? null);
  const now = useNow(30_000);
  const [, setLapseTick] = useState(0);

  useEffect(() => {
    if (!idKey) return;
    let live = true;
    const asked = idKey.split(",");
    void (async () => {
      // Never throws: a failed call comes back as ok false, reason unknown.
      const data = await getCartHoldState(asked);
      const read = { key: idKey, ...readHoldState(asked, data, Date.now()) };
      lastRead.set(idKey, read);
      if (live) setState(read);
    })();
    return () => {
      live = false;
    };
  }, [idKey, armKey]);

  // Re-render the moment the soonest countdown runs out (the 30 s tick could
  // leave "Held for 1 min" up for half a minute after the hold lapsed).
  const current = state && state.key === idKey ? state : (lastRead.get(idKey) ?? null);
  const lapseAt = current ? nextLapseAt(Object.values(current.holds), Date.now() + current.skewMs) : null;
  useEffect(() => {
    if (lapseAt === null || !current) return;
    const wait = Math.min(2_147_000_000, Math.max(0, lapseAt - (Date.now() + current.skewMs)) + 50);
    const id = setTimeout(() => setLapseTick((n) => n + 1), wait);
    return () => clearTimeout(id);
  }, [lapseAt, current]);

  return (bookingId: string) => {
    const hold: LineHold = current ? (current.holds[bookingId] ?? { kind: "unknown" }) : { kind: "pending" };
    // Date.now(), not only the 30 s tick: the lapse timer above re-renders.
    return holdChipText(hold, Math.max(now, Date.now()) + (current?.skewMs ?? 0));
  };
}
